import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

import { Alert, Button, Card, Field } from "../components/ui";
import { BETA_CALLBACK_URL } from "../features/auth/RegisterForm";
import { describeError } from "../features/auth/errors";
import { useT } from "../i18n/I18nProvider";
import { authClient } from "../lib/auth-client";

export const Route = createFileRoute("/verify-email")({
  validateSearch: z.object({ email: z.string().optional(), beta: z.boolean().optional() }),
  component: VerifyEmailPage,
});

function VerifyEmailPage() {
  const t = useT();
  const { email, beta } = Route.useSearch();
  const [typed, setTyped] = useState("");
  const [notice, setNotice] = useState<{ kind: "success" | "error"; text: string } | null>(null);

  /**
   * The address comes from the link when the customer arrived here from registering, and from
   * the field otherwise. The field is why: the resend button used to render only when `?email=`
   * was in the URL, and nothing on the page let anyone supply it — so a customer who reached
   * this page any other way, or came back to it later, had no way to ask for a new link. The
   * confirmation link expires in an hour, so coming back later is the ordinary case, not the
   * edge one.
   */
  const address = email ?? typed.trim();

  const resend = async () => {
    if (address === "") return;
    setNotice(null);
    const result = await authClient.sendVerificationEmail({
      email: address,
      callbackURL: beta === true ? BETA_CALLBACK_URL : "/app",
    });
    setNotice(
      result.error
        ? { kind: "error", text: describeError(t, result.error) }
        : { kind: "success", text: t("auth.verify.resent") },
    );
  };

  return (
    <main className="mx-auto w-full max-w-md px-4 py-10">
      <Card title={t("auth.verify.title")}>
        <p>{email ? t("auth.verify.body", { email }) : t("auth.verify.bodyUnknown")}</p>
        {notice ? <Alert kind={notice.kind}>{notice.text}</Alert> : null}
        {email ? null : (
          <Field
            label={t("common.email")}
            type="email"
            name="email"
            autoComplete="email"
            value={typed}
            onChange={(event) => {
              setTyped(event.target.value);
            }}
          />
        )}
        <Button variant="secondary" disabled={address === ""} onClick={() => void resend()}>
          {t("auth.verify.resend")}
        </Button>
        <p className="mt-4 flex flex-col gap-1 text-sm">
          <Link to="/login">{t("nav.signIn")}</Link>
        </p>
      </Card>
    </main>
  );
}
